/**
 * Draft-key reconciliation for the day ordering grid.
 *
 * Draft lines are keyed `date::mealtime::columnId::menuItemId` at the moment
 * the admin types, and the store keeps them verbatim (sessionStorage
 * persistence included). The GRID, however, re-derives its columns and items
 * from live payloads on every fetch — and both identities are known to
 * drift: column ids moved wholesale in the band cutover (the checkout page
 * already reconciles "legacy line keys" via `checkoutColumnsForLines`), and
 * a menu can hold duplicate same-label items whose visible representative
 * can change between payloads. When that happens the cells read empty while
 * the day count and "Add meals to cart" still see the stored lines — typed
 * numbers silently vanish from view (Hillside, 2026-08-31).
 *
 * `reconcileDraftDay` folds a day's stranded TYPED lines back onto the
 * rendered grid when the mapping is unambiguous:
 *   - column: an unchanged columnId wins; else the rendered column that
 *     covers the stored campus-tier id (`coveredLocationGradeRangeIds`, the
 *     band fold) or carries the stored `gradeRangeId` as its own tier.
 *   - item: an unchanged menuItemId wins; else the mealtime's unique
 *     rendered item with the same display NAME and the same system-milk-ness.
 *   - the target cell must be allowed by the column gate and must not
 *     already hold a draft line (never merge quantities — summing into an
 *     existing line is how Larkspur's launch orders doubled).
 * Anything still unmatched is reported, never silently dropped — except
 * stranded SUGGESTED lines, which are machine-derived and regenerable, so
 * they are dropped rather than left to haunt the day count.
 */

import type { SchoolCartLine } from '@/lib/school-orders/cart'
import { cartLineKey } from '@/lib/school-orders/cart'
import { isItemAllowedForColumn, type ItemColumnScope, type OrderColumn } from '@/lib/school-orders/columns'

/** The slice of a rendered menu item the reconciler needs. */
export interface ReconcileItem extends ItemColumnScope {
  id: string
  name: string
  systemMilk: boolean
}

export interface ReconcileMealtimeGrid {
  mealtime: string
  columns: readonly OrderColumn[]
  items: readonly ReconcileItem[]
}

export interface DraftMove {
  fromKey: string
  line: SchoolCartLine
}

export interface DraftReconcileResult {
  /** Unambiguous rewrites: stranded typed line → rendered cell. */
  moves: DraftMove[]
  /** Stranded machine-derived lines to drop (regenerable suggestions). */
  dropKeys: string[]
  /** Typed lines that stay stranded — surfaced, never silently hidden. */
  unmatchedKeys: string[]
  /** MEAL quantity of the unmatched lines (system milk excluded). */
  unmatchedMealQty: number
}

const EMPTY_RESULT: DraftReconcileResult = { moves: [], dropKeys: [], unmatchedKeys: [], unmatchedMealQty: 0 }

/**
 * The rendered column a stranded line folds onto, or null when none/ambiguous.
 * An id match means the line is not stranded on the column axis.
 */
function resolveColumn(line: SchoolCartLine, columns: readonly OrderColumn[]): OrderColumn | null {
  const direct = columns.find((c) => c.columnId === line.columnId)
  if (direct) {
    return direct
  }
  const matches = columns.filter(
    (c) =>
      (c.coveredLocationGradeRangeIds ?? []).includes(line.columnId) ||
      (line.gradeRangeId !== null &&
        (c.gradeRangeId === line.gradeRangeId ||
          c.columnId === line.gradeRangeId ||
          (c.coveredLocationGradeRangeIds ?? []).includes(line.gradeRangeId))),
  )
  return matches.length === 1 ? matches[0]! : null
}

/** The rendered item a stranded line maps onto, or null when none/ambiguous. */
function resolveItem(line: SchoolCartLine, items: readonly ReconcileItem[]): ReconcileItem | null {
  const direct = items.find((it) => it.id === line.menuItemId)
  if (direct) {
    return direct
  }
  const lineIsMilk = line.systemMilk === true
  const matches = items.filter((it) => it.name === line.name && it.systemMilk === lineIsMilk)
  return matches.length === 1 ? matches[0]! : null
}

export function reconcileDraftDay(input: {
  /** The day's draft lines (every mealtime). */
  lines: readonly SchoolCartLine[]
  /** The grid as rendered right now, one entry per displayed mealtime. */
  grids: readonly ReconcileMealtimeGrid[]
}): DraftReconcileResult {
  const gridByMealtime = new Map(input.grids.map((g) => [g.mealtime, g]))
  const draftKeys = new Set(input.lines.map((l) => l.key))
  const moves: DraftMove[] = []
  const dropKeys: string[] = []
  const unmatched: SchoolCartLine[] = []
  // Cells already claimed by a move this pass — two stranded lines must
  // never land on the same target.
  const claimed = new Set<string>()

  for (const line of input.lines) {
    const grid = gridByMealtime.get(line.mealtime)
    if (!grid) {
      // The whole mealtime is gone from the grid (menu changed) — a typed
      // line stays visible-as-unmatched; a suggestion just goes.
      if (line.suggested) {
        dropKeys.push(line.key)
      } else {
        unmatched.push(line)
      }
      continue
    }
    const column = resolveColumn(line, grid.columns)
    const item = column ? resolveItem(line, grid.items) : null
    const stranded = !column || !item || column.columnId !== line.columnId || item.id !== line.menuItemId
    if (!stranded) {
      continue
    }
    if (line.suggested) {
      dropKeys.push(line.key)
      continue
    }
    if (!column || !item || !isItemAllowedForColumn(item, column)) {
      unmatched.push(line)
      continue
    }
    const target = cartLineKey(line.date, line.mealtime, column.columnId, item.id)
    if (draftKeys.has(target) || claimed.has(target)) {
      unmatched.push(line)
      continue
    }
    claimed.add(target)
    moves.push({
      fromKey: line.key,
      line: {
        ...line,
        key: target,
        columnId: column.columnId,
        gradeRangeId: column.gradeRangeId,
        groupDimension: column.groupDimension,
        groupValue: column.groupValue,
        menuItemId: item.id,
      },
    })
  }
  if (moves.length === 0 && dropKeys.length === 0 && unmatched.length === 0) {
    return EMPTY_RESULT
  }
  return {
    moves,
    dropKeys,
    unmatchedKeys: unmatched.map((l) => l.key),
    unmatchedMealQty: unmatched.reduce((sum, l) => sum + (l.systemMilk ? 0 : l.quantity), 0),
  }
}
